import React, {useEffect, useMemo} from "react"
import {BackButton} from "../../components/BackButton/BackButton"
import {useHistory, useLocation} from "react-router-dom"
import {useFormik} from "formik"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import * as yup from "yup"
import {Helmet} from "react-helmet-async"
import {Button, Checkbox, Form, Tag} from "antd"
import {FormInput} from "../../components/Form/FormInput"
import {FormTextarea} from "../../components/Form/FormTextarea"
import {getObjectGroups, setObjectGroups} from "../../store/objectGroupReducer"
import {getCompanies, setCompanies} from "../../store/companyReducer"
import {FormSelect} from "../../components/Form/FormSelect"
import {getObjects, setObjects} from "../../store/objectReducer"
import {changePSLeakDevice, createPSLeakDevice} from "../../store/psLeakDeviceReducer"
import {PSLeakDeviceResponse} from "../../models/response"
import {filterFromString} from "../../helps/filterFromString"

const regexIpAddress = /^(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
const regexMacAddress = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/

export const PSLeakDeviceForm = () =>
{
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation<{
        psLeakDevice: PSLeakDeviceResponse, idCompany?: number, idObjectGroup?: number, idObject?: number,
        uin?: string, name?: string | null, macAddress?: string | null
    }>()
    const name = location?.state?.name
    const macAddress = location?.state?.macAddress
    const uin = location?.state?.uin
    const device = location?.state?.psLeakDevice
    const idCompany = location?.state?.idCompany
    const idObjectGroup = location?.state?.idObjectGroup
    const idObject = location?.state?.idObject
    const status = useSelector((state: StateType) => state.appReducer.status)
    const companies = useSelector((state: StateType) => state.companyReducer.companies)
    const objectGroups = useSelector((state: StateType) => state.objectGroupReducer.objectGroups)
    const objects = useSelector((state: StateType) => state.objectReducer.objects)
    const controller = useMemo(() => new AbortController(), [])

    const schema = useMemo(() =>
    {
        let baseSchema = {
            name: yup.string().required("Введите название").max(250, "Максимальная длина 250 символов"),
            idCompany: yup.number().required("Выберите компанию").min(1, "Выберите компанию"),
            idObjectGroup: yup.number().required("Выберите группу").min(1, "Выберите компанию"),
            idObject: yup.number().required("Выберите объект").min(1, "Выберите компанию"),
            comment: yup.string().max(2000, "Максимальная длина 2000 символов"),
            ipAddress: yup.string().nullable().test("validationIpAddress", "Укажите IP адрес", function (value)
            {
                if (!value) return true
                if (regexIpAddress.test(value)) return true
                return false
            }),
            port: yup.number().nullable().min(1, "Укажите порт").max(65534, "Укажите порт"),
        }

        if (device) return yup.object().shape(baseSchema)
        else return yup.object().shape({
            ...baseSchema,
            uin: yup.string().required("Введите UIN").max(50, "Максимальная длина 50 символов"),
            macAddress: yup.string().nullable().test("validationMacAddress", "Укажите MAC адрес", function (value)
            {
                if (!value) return true
                if (regexMacAddress.test(value)) return true
                return false
            }),
        })
    }, [device])

    useEffect(() =>
    {
        dispatch(getCompanies({excludeSystem: true, controller}))

        return () =>
        {
            controller.abort()
            dispatch(setCompanies(null))
            dispatch(setObjectGroups(null))
            dispatch(setObjects(null))
        }
    }, [dispatch, controller])

    const formik = useFormik({
        initialValues: {
            name: device?.name || name || "",
            idCompany: device?.idCompany || idCompany,
            idObjectGroup: device?.idObjectGroup || idObjectGroup,
            idObject: device?.idObject || idObject,
            active: device ? device.active : true,
            comment: device?.comment || "",
            uin: device?.uin || uin || "",
            macAddress: device?.macAddress || macAddress || "",
            ipAddress: device?.ipAddress || "",
            port: device?.port || "",
            disableLocalData: device ? device.disableLocalData : false,
        },
        validationSchema: schema,
        onSubmit: async (values) =>
        {
            const request = {
                name: values.name,
                idObject: values.idObject || 0,
                active: values.active,
                comment: values.comment,
                disableLocalData: values.disableLocalData,
                ipAddress: values.ipAddress || null,
                port: values.port as number || null,
                controller,
            }

            let res: any
            if (device)
            {
                res = await dispatch(changePSLeakDevice({
                    id: device.id,
                    ...request,
                    controller,
                }))
            }
            else
            {
                res = await dispatch(createPSLeakDevice({
                    ...request,
                    uin: values.uin,
                    macAddress: values.macAddress || null,
                    controller,
                }))
            }
            if (res?.payload?.status === 0)
            {
                if (!device)
                {
                    history.replace(`/psLeakDevice/${res.payload.data.id}`)
                }
                else
                {
                    history.goBack()
                }
            }
        },
    })
    const {values, handleChange, handleBlur, handleSubmit, errors, touched, setFieldValue} = formik

    useEffect(() =>
    {
        const filterString: any = filterFromString(location.search)
        if (filterString?.uin) setFieldValue("uin", filterString.uin)
        if (filterString?.name) setFieldValue("name", filterString.name)
        if (filterString?.macAddress) setFieldValue("macAddress", filterString.macAddress)
    }, [dispatch, location])

    useEffect(() =>
    {
        if (values?.idCompany)
        {
            dispatch(getObjectGroups({idCompany: values.idCompany, controller}))
        }
    }, [dispatch, values?.idCompany, controller])

    useEffect(() =>
    {
        if (values?.idObjectGroup)
        {
            dispatch(getObjects({idObjectGroup: values.idObjectGroup, controller}))
        }
    }, [dispatch, values?.idObjectGroup, controller])

    useEffect(() =>
    {
        if (companies?.data && !values?.idCompany)
        {
            setFieldValue("idCompany", companies.data[0]?.id)
        }

        if (objectGroups?.data && !values?.idObjectGroup)
        {
            setFieldValue("idObjectGroup", objectGroups.data[0]?.id)
        }

        if (objects?.data && !values?.idObject)
        {
            setFieldValue("idObject", objects.data[0]?.id)
        }
    }, [companies, objectGroups, objects, values])

    const companyOptions = useMemo(() => companies?.data?.map(i => ({value: i.id, label: i.name})), [companies])
    const groupsOptions = useMemo(() => objectGroups?.data?.map(i => ({value: i.id, label: i.name})), [objectGroups])
    const objectsOptions = useMemo(() => objects?.data?.map(i => ({value: i.id, label: i.name})), [objects])

    return (
        <div className="form-page">
            <Helmet>
                <title>{device ? "Обновление оборудования" : "Новое оборудование"}</title>
            </Helmet>
            <Form className="form-container"
                  autoComplete="off"
                  onSubmitCapture={handleSubmit}
            >
                <div className="description-header"><BackButton onClick={() => history.goBack()}/></div>

                <div className="description-title">{device ? "Обновление оборудования" : "Новое оборудование"}</div>

                <FormInput placeholder="Название"
                           value={values.name}
                           name="name"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.name && errors.name}
                           allowClear
                />

                <FormInput placeholder="UIN"
                           value={values.uin}
                           name="uin"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.uin && errors.uin}
                           allowClear
                           disabled={!!device}
                />

                <FormSelect placeholder="Компания"
                            value={values.idCompany || ""}
                            onChange={(value) =>
                            {
                                dispatch(setObjectGroups(null))
                                dispatch(setObjects(null))
                                setFieldValue("idCompany", value)
                                setFieldValue("idObjectGroup", null)
                                setFieldValue("idObject", null)
                            }}
                            options={companyOptions}
                            className="form-select"
                            isInvalid={touched.idCompany && errors.idCompany}
                />

                <FormSelect placeholder="Группа"
                            value={values.idObjectGroup || ""}
                            onChange={(value) =>
                            {
                                dispatch(setObjects(null))
                                setFieldValue("idObjectGroup", value)
                                setFieldValue("idObject", null)
                            }}
                            options={groupsOptions}
                            className="form-select"
                            isInvalid={touched.idObjectGroup && errors.idObjectGroup}
                />

                <FormSelect placeholder="Объект"
                            value={values.idObject || ""}
                            onChange={(value) => setFieldValue("idObject", value)}
                            options={objectsOptions}
                            className="form-select"
                            isInvalid={touched.idObject && errors.idObject}
                />

                <FormInput placeholder="MAC адресс"
                           value={values.macAddress}
                           name="macAddress"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.macAddress && errors.macAddress}
                           allowClear
                           disabled={!!device}
                />

                <FormInput placeholder="IP адресс"
                           value={values.ipAddress}
                           name="ipAddress"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.ipAddress && errors.ipAddress}
                           allowClear
                />

                <FormInput placeholder="Порт"
                           value={values.port}
                           name="port"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.port && errors.port}
                           allowClear
                />

                <FormTextarea placeholder="Комментарий"
                              value={values.comment}
                              name="comment"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              isInvalid={touched.comment && errors.comment}
                              allowClear
                              autoSize={{minRows: 2}}
                />

                <div style={{marginTop: 20}}>
                    <Checkbox checked={values.active}
                              name="active"
                              onChange={handleChange}
                    >
                        <Tag color={values.active ? "green" : "default"}>Активно</Tag>
                    </Checkbox>
                </div>

                <div style={{marginTop: 20}}>
                    <Checkbox checked={values.disableLocalData}
                              name="disableLocalData"
                              onChange={handleChange}
                    >
                        <Tag color={values.disableLocalData ? "gold" : "default"}>Не принимать локальные данные</Tag>
                    </Checkbox>
                </div>

                <div className="form-button-block">
                    <Button type="primary"
                            htmlType="submit"
                            loading={status === "loading"}
                    >
                        Сохранить
                    </Button>
                    <Button type="default"
                            onClick={() => history.goBack()}
                    >
                        Отмена
                    </Button>
                </div>


            </Form>
        </div>
    )
}

